import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';

const formatTitle = (row = {}) =>
  row.event_title || row.eventTitle || row.event_description || 'Udyog Connect Event';

const formatPayment = (snapshot = {}) => {
  if (!snapshot?.enabled) return 'Free';
  const total = Number(snapshot.totalUsd || 0);
  const status = snapshot.status || 'payment_not_collected';
  return `$${total.toFixed(2)} · ${status.replace(/_/g, ' ')}`;
};

const EventRegistrationsManager = () => {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [message, setMessage] = useState('');
  const [sortModel, setSortModel] = useState([{ field: 'registered_at', sort: 'desc' }]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const fetchRegistrations = useCallback(() => {
    setLoading(true);
    setMessage('');
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/event-registrations`, { headers })
      .then((res) => {
        setRegistrations(Array.isArray(res.data?.registrations) ? res.data.registrations : []);
      })
      .catch((err) => {
        console.error('Error loading event registrations', err);
        setMessage(err?.response?.data?.message || 'Unable to load event registrations.');
      })
      .finally(() => setLoading(false));
  }, [headers]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  const filteredRows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return registrations;
    return registrations.filter((row) =>
      [
        formatTitle(row),
        row.event_category,
        row.full_name,
        row.email,
        row.phone,
        row.city_zip,
        row.primary_interest,
        row.notes,
        row.status,
        row.event_venue,
        row.slot_label,
        row.slot_date,
        JSON.stringify(row.customAnswers || {}),
        formatPayment(row.paymentSnapshot || {}),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }, [filter, registrations]);

  const exportCsv = () => {
    const headers = [
      'Registered At',
      'Event',
      'Event Date',
      'Selected Slot',
      'Slot Date / Time',
      'Name',
      'Email',
      'Phone',
      'City/ZIP',
      'Seats',
      'Interest',
      'Payment',
      'Status',
      'Custom Answers',
      'Notes',
    ];
    const escapeCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = [
      headers.map(escapeCell).join(','),
      ...filteredRows.map((row) =>
        [
          row.registered_at,
          formatTitle(row),
          row.event_date,
          row.slot_label || '',
          row.slot_date ? `${row.slot_date} ${row.slot_start_time || ''}-${row.slot_end_time || ''}` : '',
          row.full_name,
          row.email,
          row.phone,
          row.city_zip,
          row.seats_requested,
          row.primary_interest,
          formatPayment(row.paymentSnapshot || {}),
          row.status,
          JSON.stringify(row.customAnswers || {}),
          row.notes,
        ]
          .map(escapeCell)
          .join(',')
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `uc-event-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const columns = [
    { field: 'registered_at', headerName: 'Registered', minWidth: 170, flex: 0.7 },
    {
      field: 'event_title',
      headerName: 'Event',
      minWidth: 260,
      flex: 1.2,
      valueGetter: (_, row) => formatTitle(row),
      renderCell: (params) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: '#0f172a', fontWeight: 900, lineHeight: 1.25 }}>
            {formatTitle(params.row)}
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
            {params.row.event_date || 'Date TBA'} · {params.row.event_venue || 'Venue TBA'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'slot_label',
      headerName: 'Selected Slot',
      minWidth: 220,
      flex: 0.9,
      renderCell: (params) => params.value ? (
        <Box>
          <Typography sx={{ color: '#0f172a', fontWeight: 850 }}>{params.value}</Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            {params.row.slot_date} · {params.row.slot_start_time}–{params.row.slot_end_time}
          </Typography>
        </Box>
      ) : <Typography variant="caption" color="text.secondary">Single event time</Typography>,
    },
    {
      field: 'full_name',
      headerName: 'Registrant',
      minWidth: 220,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: '#0f172a', fontWeight: 900 }}>{params.value}</Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>{params.row.email}</Typography>
        </Box>
      ),
    },
    { field: 'phone', headerName: 'Phone', minWidth: 140, flex: 0.6 },
    { field: 'city_zip', headerName: 'City / ZIP', minWidth: 150, flex: 0.7 },
    { field: 'seats_requested', headerName: 'Seats', minWidth: 90, flex: 0.35 },
    { field: 'primary_interest', headerName: 'Interest', minWidth: 230, flex: 1 },
    {
      field: 'paymentSnapshot',
      headerName: 'Payment',
      minWidth: 180,
      flex: 0.8,
      valueGetter: (_, row) => formatPayment(row.paymentSnapshot || {}),
      renderCell: (params) => (
        <Chip
          size="small"
          label={formatPayment(params.row.paymentSnapshot || {})}
          color={params.row.paymentSnapshot?.enabled ? 'warning' : 'default'}
          variant={params.row.paymentSnapshot?.enabled ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 130,
      flex: 0.55,
      renderCell: (params) => (
        <Chip
          size="small"
          label={String(params.value || 'registered').replace(/_/g, ' ')}
          color={params.value === 'registered' ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'customAnswers',
      headerName: 'Custom Answers',
      minWidth: 260,
      flex: 1,
      valueGetter: (_, row) => JSON.stringify(row.customAnswers || {}),
    },
  ];

  const slotGroups = useMemo(() => {
    const groups = new Map();
    filteredRows.filter((row) => row.registrationType === 'event').forEach((row) => {
      const key = `${row.event_id || ''}|${row.slot_id || 'single'}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          event: formatTitle(row),
          slot: row.slot_label || 'Single event time',
          date: row.slot_date || row.event_date,
          time: row.slot_start_time
            ? `${row.slot_start_time}–${row.slot_end_time || ''}`
            : `${row.event_start_time || ''}–${row.event_end_time || ''}`,
          registrations: 0,
          seats: 0,
          capacity: row.slot_capacity,
        });
      }
      const group = groups.get(key);
      group.registrations += 1;
      group.seats += Number(row.seats_requested || 0);
    });
    return [...groups.values()].sort((a, b) => `${a.event}|${a.date}|${a.time}`.localeCompare(`${b.event}|${b.date}|${b.time}`));
  }, [filteredRows]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            Event & Meetup Registrations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review native UC event and community-meetup registrations, including future UC meetup signups.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`${registrations.length} total registrations`} color="primary" variant="outlined" />
          <Button variant="outlined" onClick={fetchRegistrations}>Refresh</Button>
          <Button variant="contained" onClick={exportCsv} disabled={!filteredRows.length}>Export CSV</Button>
        </Stack>
      </Box>

      {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}

      {slotGroups.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #b9ddd9', borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 900, mb: 1 }}>Registrations grouped by event and slot</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
            {slotGroups.map((group) => (
              <Box key={group.key} sx={{ p: 1.25, border: '1px solid #dce9e7', borderRadius: 2, bgcolor: '#f7fcfb' }}>
                <Typography sx={{ fontWeight: 850 }}>{group.event}</Typography>
                <Typography variant="body2">{group.slot} · {group.date} {group.time}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {group.registrations} registration{group.registrations === 1 ? '' : 's'} · {group.seats}{group.capacity ? ` of ${group.capacity}` : ''} seats
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      <Paper elevation={0} sx={{ p: 2, border: '1px solid #dbeafe', borderRadius: 3 }}>
        <TextField
          fullWidth
          size="small"
          label="Search event and meetup registrations"
          placeholder="Search name, email, event, meetup, interest, payment, custom answers..."
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          sx={{ mb: 2 }}
        />

        {loading ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: 'clamp(520px, 62vh, 720px)', width: '100%' }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              getRowId={(row) => row.id}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50, 100]}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              disableRowSelectionOnClick
              rowHeight={76}
              columnHeaderHeight={56}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 3,
                '& .MuiDataGrid-columnHeaders': {
                  background: 'linear-gradient(180deg, #f8fafc 0%, #eef7f6 100%)',
                  color: '#0f172a',
                },
                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 950 },
                '& .MuiDataGrid-row:hover': { bgcolor: '#f0fdfa' },
              }}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default EventRegistrationsManager;
